import { IAnnotation, ISchemaObject, IItemDelta, ISignal } from '@augloop/types-core';
import { IDynamicTextContext } from '@augloop/types-text';
import { IClientMetadata } from '@augloop/schemas/dist/src/client/schemas';
import { AuthTokenType, UserType } from './authTypes';
import { SerializationProfile } from './ipubsub';

export enum WorkflowKind {
    SingleItem,
    Reduce,
    Grid,
    DynamicText
}

export const enum InputStage {
    PreSeed = 1,  // Trigger on seed content as soon as it arrives.
    OnSeed = 2, // Trigger when all seed content has arrived (implied & optional if PreSeed is set).
    PostSeed = 4, // Trigger on post-seed incremental changes.
    All = PreSeed | PostSeed // Trigger on any content as soon as it arrives.
}

export const enum ActivationTier {
    PreActivate = 0,
    Default = 1,
    DelayActivate = 1,
    NeverActivate = 2
}

export const enum TriggerCondition {
    UpstreamWorkflowsReady = 0, // Workflow will not be triggered if upstream workflows have not finished execution.
    /**
     * In addition to default workflow triggering also trigger workflows when metadata is updated.
     * By default workflows are not triggered on metadata updates.
     */
    AnnotationMetadataUpdated = 1
}

export const enum UnitType {
    Character = 1
}

export type WorkflowId = string;

export interface IActivationConfig {
    /**
     * Supported ClientAppNames:
     * 'All' or specific client app name.
     */
    clientAppName: string;

    /**
     * Supported ClientAppPlatforms:
     * 'All' or specific client app platform.
     */
    clientAppPlatform: string;

    activationTier: ActivationTier;
}

export interface IActivationFlightsConfig {
    /**
     * Supported ClientAppNames:
     * 'All' or specific client app name.
     */
    clientAppName: string;
    /**
     * Required flights for a given client app
     */
    requiredFlights: string[];
    /**
     * Restricted flights for a given client app
     */
    restrictedFlights?: string[];
}

export interface IDynamicExecutionPreferences {
    /**
     * Unit type to measure input size
     */
    inputSizeUnit: UnitType;

    /**
     * Preferred input size for each batch
     * Consecutive changed items will be batched by the preferred input size.
     * In cases with fewer consecutive changes, the actual input size might be smaller than the preferred one.
     * Item on the boundary causing actual input size exceeds the preferred one will be kept in the current batch.
     */
    inputSize: number;

    /**
     * Unit type to measure context size, e.g., character for text context case.
     */
    contextUnit?: UnitType;

    /**
     * Preferred context size above input (having the same parent path) to be retrieved for dynamic text workflow
     *
     * These rules apply to other context sizes as well:
     * Item on the boundary causing context size greater than the preferred one will be retrieved.
     * If marked -1, all items meet the condition will be retrieved.
     * Actual context size might be smaller than the preferred one if the content is not enough for given scope.
     *
     * In the future, more properties can be added for multidimensional cases. For example, contextLeft, contextRight, etc.
     */
    contextAbove?: number;

    /**
     * Preferred context size below input (having the same parent path) to be retrieved for dynamic text workflow
     */
    contextBelow?: number;
}

export type SetAnnotationsOptions = {
    /**
     * If set, attaches the annotations to the first ancestor of the provided object that matches this ancestor
     * type, instead of attaching them to the object itself. If no such ancestor exists, the workflow fails and
     * all annotations produced by this workflow will be discarded.
     */
    ancestorType?: string;

    /**
     * If set applies the annotation directly on the session object.
     * Note: If a parent object is supplied for this annotation it will be ignored.
     */
    isSessionAnnotation?: boolean;

    /**
     * If set applies the annotation immediately outside of the workflow context
     * Does not wait for workflow to complete
     * Can be called after a workflow is done.
     */
    immediate?: boolean;
};

/**
 * Contains all actions a workflow can take while executing.
 */
export interface IWorkflowActions {
    /**
     * Attaches annotations of a given type to the provided object, replacing any annotations of this type that
     * this workflow has produced previously (including via earlier calls to this method in the same workflow
     * execution). Pass an empty array to remove all annotations of this type produced by this workflow.
     * See the SetAnnotationsOptions type for additional variations.
     */
    setAnnotations(obj: ISchemaObject, annotationType: string, annotations: IAnnotation[], options?: SetAnnotationsOptions): void;

    /**
     * Adds an annotation to the provided object. Calling this method multiple times will append all annotations
     * added into a single list and apply them together to the model as well as send them back the client if they have
     * been activated.
     *
     * Every workflow execution starts with a clean slate with regard to its output (except if the workflow sets keepExistingAnnotations to true);
     * annotations should be added the same way, whether this is the first execution in a session or not. The workflow runtime will take care of
     * appropriately diff'ing the output against the actual state of the session data model and updating the client accordingly.
     *
     * @param obj The object to attach the provided annotation to.
     * @param annotation The annotation to attach. If more than one annotation need to be attached this method can be called multiple times.
     *
     * NOT YET IMPLEMENTED
     */
    addAnnotation(obj: ISchemaObject, annotation: IAnnotation): void;

    /**
     * Adds an annotation as a sibling to the provided annotation object. Calling this method multiple times
     * will append all annotations added into a single list and apply them together to the model as well as send them back
     * the client if they have been activated.
     *
     * Every workflow execution starts with a clean slate with regard to its output (except if the workflow sets keepExistingAnnotations to true);
     * annotations should be added the same way, whether this is the first execution in a session or not. The workflow runtime will take care of
     * appropriately diff'ing the output against the actual state of the session data model and updating the client accordingly.
     *
     * @param obj The object to which the provided annotation should be attached as a sibling.
     * @param annotation The annotation to attach. If more than one annotation need to be attached this method can be called multiple times.
     *
     * NOT YET IMPLEMENTED
     */
    addSiblingAnnotation(obj: IAnnotation, annotation: IAnnotation): void;

    /**
     * Adds an annotation to the session object. Calling this method multiple times will append all annotations
     * added into a single list and apply them together to the model as well as send them back the client if they have been activated.
     *
     * Every workflow execution starts with a clean slate with regard to its output (except if the workflow sets keepExistingAnnotations to true);
     * annotations should be added the same way, whether this is the first execution in a session or not. The workflow runtime will take care of
     * appropriately diff'ing the output against the actual state of the session data model and updating the client accordingly.
     *
     * @param annotation The annotation to attach to the session. If more than one annotation need to be attached this method can be called
     * multiple times.
     *
     * NOT YET IMPLEMENTED
     */
    addSessionAnnotation(annotation: IAnnotation): void;

    /**
    * Submits the given signals which can trigger other workflows
    */
    submitSignals(signals: ISignal[]): void;

    /**
     * Signals the end of workflow execution. Must be called once (and only once) at the end of the workflow
     * lambda to trigger sending the results back to the host.
     *
     * @param error Optional parameter to declare that this workflow did not successfully complete. If set,
     * the annotations produced by this workflow will be discarded (unless overriden by the strategy parameter).
     *
     * @param strategy Optional parameter to declare what happens next, including what to do with any queued annotations.
     */
    done(error?: Error, strategy?: AnnotationErrorStrategy): void;
}

/** Declares what happens when signaling the end of workflow execution with an error. */
export interface AnnotationErrorStrategy {
    /** When true causes queued annotations to be immediately sent, otherwise they are discarded. */
    preserveQueued?: boolean;
}

/**
 * Contatins user information for workflow execution
 */
export interface IUserContext {
    /**
     * User id extracted from token. Oid is only available when the user has been authenticated with the AL token. Workflows that require
     * the oid to be set can do so by requiring a token for their workflow execution.
     */
    oid?: string;

    /**
     * Tenant id extracted from token. For MSA users the tenant id will always be '84df9e7f-e9f6-40af-b435-aaaaaaaaaaaa'.
     * Tid is only available when the user has been authenticated with the AL token. Workflows that require
     * the oid to be set can do so by requiring a token for their workflow execution.
     */
    tid?: string;

    /**
     * User PUID extracted from token. PUID is only available when the user has been authenticated with the AL token. Workflows that require
     * the PUID to be set can do so by requiring a token for their workflow execution.
     */
    puid?: string;

    /**
    * User type (Consumer/enterprise) information obtained from parsing token. User is marked consumer if tenant is belongs to
    * consumer tenant id and marked as Enterprise for other tenant ids. It is marked unknown if no tenant id found.
    */
    userType?: UserType;
}

/**
 * Provides a mechanism for releasing resources
 */
export interface IDisposable {
    /**
     * Performs tasks associated with freeing, releaseing or resetting of resources.
     */
    dispose(): void;
}

/**
 * Context data for blob storage access
 */
export interface IBlobContext {
    readonly sessionKey: string;
    readonly userId: string;
}

/**
 * Interacts with other locations for the workflow.
 */
export interface IWorkflowSite {
    /**
     * Retrieves model from CDN or IndexedDB
     *
     * @param modelId Name of model to be retrieved
     * @param version Version of model
     *
     * @returns Model
     */
    getResourceAsArrayBuffer(modelId: string, version: string): Promise<{ [key: string]: ArrayBuffer }>;

    /**
     * Retrieves model from CDN or IndexedDB
     *
     * @param modelId Name of model to be retrieved
     * @param version Version of model
     *
     * @returns Model
     */
    getResourceAsURL(modelId: string, version: string): Promise<{ [key: string]: string }>;
}

/**
 * Contains context information for workflow execution.
 */
export interface IWorkflowContext {
    /**
     * Client metadata - eg application name, platform, version, etc...
     */
    readonly clientMetadata: IClientMetadata;

    /**
     * User information relevant for workflow execution.
     */
    readonly userContext?: IUserContext;

    /**
     * Contains context data for blob storage access
     */
    readonly blobContext?: IBlobContext;

    /**
     * Gets a temp directory associated with this session.
     * Disabled in production environments
     */
    readonly sessionTempDir?: string;

    /**
     * Signals that triggered execution of this workflow.
     * In case workflow is triggered as part of activation, all trigger signals that arrived up until that moment will be passed to the workflow.
     * In case workflow is triggered after activation, only signal that initiated current run will be passed to the workflow.
     */
    readonly triggerSignals?: ISignal[];

    /**
     * If the input for the workflow was constructed using a delta object, the delta is also added to the context for reference.
     */
    readonly delta?: IItemDelta;

    /**
     * The existing annotations generated by this workflow on the previous input.
     * These will only be added if the workflow sets the fetchExistingAnnotations flag.
     */
    readonly existingAnnotations?: IAnnotation[];

    /**
     * Fetches authentication token of requested type.
     */
    getToken(tokenType: AuthTokenType, cb: (error: Error, token: string, returnedTokenType?: AuthTokenType) => void): void;

    /**
     * Provides utility services to the workflow.
     */
    site?: IWorkflowSite;

}

/**
 * Contains all context information for dynamic text workflow execution.
 * Similarly, we'll have IDynamicGridWorkflowContext, IDynamicInkWorkflowContext, etc., in the future.
 */
export interface IDynamicTextWorkflowContext extends IWorkflowContext {
    /**
     * Contains context items for dynamic workflow execution on text.
     */
    readonly dynamicTextContext?: IDynamicTextContext;
}

/**
 * Core workflow definition that contains all information for workflow description that is common across all workflow types.
 * Internal use only.
 * Interface is used by ServiceFabric\gulpfile.js. Changes here might require an update in the gulp file.
 */

export interface IWorkflowDefinition {
    /**
     * The ID of the workflow. This will be used for setting annotation IDs produced by
     * this workflow. Should have at most 40 characters and use PascalCasing.
     */
    readonly id: string;

    /**
     * The resource ID of the log. This will be used for logging produced by
     * this workflow. Should have at most 40 characters and use PascalCasing.
     */
    readonly resourceId?: string;

    /**
     * The workflow kind, e.g. single-item workflow or reduce workflow.
     */
    readonly kind: WorkflowKind;

    /**
     * The relative priority. The default is 0; specify a negative number to run at higher priority
     * than the default or a positive number to run at a lower priority. When a session model update
     * triggers multiple workflows, higher-priority workflows will execute first.
     */
    readonly priority?: number;

    /**
     * Each element in activationConfigs contains clientAppName, clientAppPlatform and activationTier.
     * Workflow authors or users can specify activation tier for different client app & platform combinations.
     * ActivationConfigs can be overriden for specific apps/platforms in sequence.
     *
     * For given client app & platform, workflow in PreActivate tier will be activated immediately when the
     * session is initialized, whereas workflow in DelayActivate tier will be held in inactivate status until
     * its output annotation(s) is(are) directly or indirectly activated. The default behavior is DelayActivate.
     *
     * ActivationTier will be Default for app & platform combinations not explicitly mentioned in activationConfigs.
     */
    readonly activationConfigs?: IActivationConfig[];

    /**
     * The scope of the input for collection workflows (e.g. reduce).
     * This has to be one of the getTypeName() strings on our predefined objects.
     * Ex: If the reduce workflow wants all input types in the document then this scope would be
     * Document.getTypeName() from @augloop/types-core
     */
    readonly collectionScopeType?: string;

    /**
     * The list of input types the workflow accepts.
     * This has to be one or more of the getTypeName() strings on our predefined objects.
     * Ex: TextTile.getTypeName() from @augloop/types-text
     * Setting trigger signals will cause the workflow to no longer trigger on addition/update of input types
     */
    readonly inputTypes: string[];

    /**
     * The stage at which input is received (all stages, seeding only, live updates only).
     */
    readonly inputStage?: InputStage;

    /**
     * The list of signal types which will activate execution of the workflow.
     * In case trigger signal types list is empty, workflow will trigger based on inputTypes
     * This has to be one or more of the getTypeName() strings on our predefined objects.
     * Ex: SimilarityCheckSignal.getTypeName() from @augloop/types-signals
     */
    readonly triggerSignals?: string[];


    /**
     * An array of additional conditions to be checked before triggering workflow.
     * Workflow will not be triggered unless all listed conditions are met.
     * If triggerConditions is undefined or empty, the behavior is equivalent to the default.
     */
    readonly triggerConditions?: TriggerCondition[];

    /**
     * The list of output (annotation) types this workflow is expected to produce.
     * This has to be one or more of the getTypeName() strings on our predefined objects.
     * Ex: RangeAnnotation.getTypeName() from @augloop/types-text
     */
    readonly outputTypes: string[];

    /**
     * The list of auth token types required for proper execution of the workflow.
     * If the tokens are not available, will try alternate tokens, otherwise workflow will not be executed.
     * Optional tokens and required tokens cannot be used in the same workflow at this time.
     */
    readonly requiredTokenTypes?: AuthTokenType[];

    /**
     * The list of auth token types that the workflow can try to use.
     * If the tokens are not available, the workflow will still execute.
     * Optional tokens and required tokens cannot be used in the same workflow at this time.
     */
    readonly optionalTokenTypes?: AuthTokenType[];

    /**
     * Each element in activationFlightsConfig contains clientAppName and array of requiredFlights for a given clientAppName.
     * Workflow authors or users can specify activation flights for a given client app name (or 'All' which sets required flights for all clients).
     *
     * Workflow will be trigered if flights provided by client contains all required flights for a given client name
     */
    readonly activationFlightsConfigs?: IActivationFlightsConfig[];

    /**
     * Whether the workflow is allowed to use appOnly token when required user token doesn't exist
     */
    readonly isAppOnlyTokenAllowed?: boolean;

    /**
     * Minimum delay for triggering workflow after input changes.
     */
    readonly minDelayMs?: number;

    /**
     * Maximum delay for triggering workflow after input changes.
     */
    readonly maxDelayMs?: number;

    /**
     * Maximum execution duration of workflow.
     * Used only in cases when workflow expect execution to be longer than default timeout.
     * Default timeout is restricted by pubsub timeout - 8 seconds - pubSubHttpSendTimeout.
     * Applies only for workflow as a service.
     */
    readonly maxExecutionTimeInS?: number;

    /**
     * Maximum wait time on the queue for a workflow before skipping its execution.
     * Useful for cases where workflow results are time-sensitive.
     */
    readonly maxQueueWaitTimeMs?: number;

    /**
     * Indicates which serializer the runtime should use when communicating with the workflow
     */
    readonly serializationProfile?: SerializationProfile;

    /**
     * Window size (number of signals) to be queried from SignalStore
     * Used only for signal workflows
     * Queries the most recent signals sent to the SignalStore
     * Only one of windowSize and time frame should be set at a time. If both are set,
     * workflow will query by windowSize. If neither are set, workflow will query all
     * signals of the type specified within the workflow.
     */
    readonly windowSize?: number;

    /**
     * Time frame to be queried from SignalStore in milliseconds
     * Used only for signal workflows
     * Queries the most recent signals within time frame sent to the SignalStore
     * Only one of windowSize and time frame should be set at a time. If both are set,
     * workflow will query by windowSize. If neither are set, workflow will query all
     * signals of the type specified within the workflow.
     */
    readonly timeFrame?: number;

    /**
     * Marks this workflow as stateful. Only one instance will be instantiated for this workflow per session and all
     * invocations will be directed to this instance.
     */
    readonly isStateful?: boolean;

    /**
     * how long to wait before purging the workflow state for a session
     */
    readonly stateExpiryMs?: number;

    /**
     * Marks this annotations that the workflow produces to bypass the model.
     * Annotations will all be sent as adds.
     */
    readonly bypassModel?: boolean;

    /**
     * This option only works for Reduce Workflows. It specifies the workflow may add annotations to any of its inputs.
     * The default value is false which implies the workflow is only annotating the scope element.
     * Setting this flag dictates how the workflow output is compared with the existing session data model state.
     */
    readonly annotatesSubtree?: boolean;

    /**
     * Indicates whether any previous annotations produced by this workflow should be erased before applying the new output.
     * The default value for this flag is false. Setting this flag to true means the workflow's output will be appended to any
     * output it produces in previous instantiations instead of replacing it.
     */
    readonly keepExistingAnnotations?: boolean;

    /**
     * This option only works for Grid Neighborhood Workflows. The number of rows above a cell to retrieve from.
     * If left undefined, the workflow will retrieve zero rows above for the neighborhood. If marked -1,
     * the neighborhood will retrieve all rows before the target cell.
     */
    readonly rowsBefore?: number;

    /**
     * This option only works for Grid Neighborhood Workflows. The number of rows below a cell to retrieve from.
     * If left undefined, the workflow will retrieve zero rows below for the neighborhood. If marked -1,
     * the neighborhood will retrieve all rows after the target cell.
     */
    readonly rowsAfter?: number;

    /**
     * This option only works for Grid Neighborhood Workflows. The number of columns before a cell to retrieve from.
     * If left undefined, the workflow will retrieve zero columns before for the neighborhood. If marked -1,
     * the neighborhood will retrieve all columns before the target cell.
     */
    readonly columnsBefore?: number;

    /**
     * This option only works for Grid Neighborhood Workflows. The number of columns after a cell to retrieve from.
     * If left undefined, the workflow will retrieve zero columns after for the neighborhood. If marked -1,
     * the neighborhood will retrieve all columns after the target cell.
     */
    readonly columnsAfter?: number;

    /**
     * This option only works for Grid Neighborhood Workflows.
     * If this flag enabled the workflow will get all the worksheet data in single execution (instead of chunks) if the number of non-empty cells is less than workflowMaxItemsSetting.
     * Otherwise the workflow won't execute at all.
     */
    readonly isFullWorksheetRequired?: boolean;

    /**
     * This option only works for dynamic workflows.
     * Individual items will be batched, and potential context will be provided for dynamic workflow lambda function by preferences.
     */
    readonly dynamicExecutionPreferences?: IDynamicExecutionPreferences;

    /**
     * Whether to fetch annotations produced by this workflow for the previous input. The annotations are fetched as
     * best effort and are not guaranteed to be available. The workflow will still execute even if fetching the annotations
     * failed.
     * The annotations will be added in the workflow context and they will only be added if this flag is set to true
     */
    readonly fetchExistingAnnotations?: boolean;
}

/**
 * Definition of a workflow execution functionality.
 * Workflow has to inherit from this interface and provide all the information
 * in IWorkflowDefinition and IWorkflow.
 */
export interface IWorkflow extends IWorkflowDefinition {
    /**
     * The factory which creates the workflow instances.
     */
    readonly factory: () => IDisposable;
}
